import { TooltipPosition } from "@/integrations/captainup/CaptainUpTypes";

export type HostSessionPhase =
	| "disconnected"
	| "transport_ready"
	| "configured"
	| "game_ready";

export type HostPlatform = "desktop" | "mobile";
export type HostOrientation = "landscape" | "portrait";

export type HostProviderBootInput = {
	sessionKey: string;
	serverUrl: string;
	gameName: string;
	lang: string;
	operator: string;
	platform: HostPlatform;
	orientation: HostOrientation;
	integrationType?: string;
};

export type HostSessionConfig = {
	sessionKey: string;
	serverUrl: string;
	gameName: string;
	lang: string;
	currency: string;
	operator: string;
	platform: HostPlatform;
	orientation: HostOrientation;
	integrationType?: string;
	isHomeButtonEnabled?: boolean;

	bet?: number;
	subunit?: number;
	funMode?: boolean;
	funModeBalance?: number;
	regulationType?: string;
	iframeContainerId?: string;
};

export type HostFreeArea = {
	version: string;
	id: string;
	gameName: string;
	x: number;
	y: number;
	width: number;
	height: number;
	platform: HostPlatform;
	orientation: HostOrientation;
	tooltipPosition?: TooltipPosition;
};

export type HostSessionState = {
	phase: HostSessionPhase;
	transportReady: boolean;
	configured: boolean;
	gameReady: boolean;

	config: HostSessionConfig | null;
	freeAreas: HostFreeArea[];
	lastUpdatedAtMs: number | null;
};

export type HostSessionSnapshot = {
	readonly phase: HostSessionPhase;
	readonly transportReady: boolean;
	readonly configured: boolean;
	readonly gameReady: boolean;

	readonly config: Readonly<HostSessionConfig> | null;
	readonly freeAreas: readonly Readonly<HostFreeArea>[];
	readonly lastUpdatedAtMs: number | null;
};

export type HostSessionListener = (
	nextState: HostSessionSnapshot,
	prevState: HostSessionSnapshot
) => void;

export type HostSessionEvent =
	| { type: "TRANSPORT_READY" }
	| {
			type: "CONFIG_RECEIVED";
			payload: {
				config: HostSessionConfig;
				freeAreas: HostFreeArea[];
			};
	  }
	| {
			type: "FREE_AREAS_UPDATED";
			payload: {
				freeAreas: HostFreeArea[];
			};
	  }
	| {
			type: "ORIENTATION_UPDATED";
			payload: {
				orientation: HostOrientation;
			};
	  }
	| { type: "GAME_READY_RECEIVED" }
	| { type: "RESET"; reason: string };

export function createInitialHostSessionState(): HostSessionState {
	return {
		phase: "disconnected",
		transportReady: false,
		configured: false,
		gameReady: false,
		config: null,
		freeAreas: [],
		lastUpdatedAtMs: null,
	};
}

export function isHostConfigured(state: HostSessionSnapshot): boolean {
	return state.configured && state.config !== null;
}

export function isHostGameReady(state: HostSessionSnapshot): boolean {
	return state.gameReady;
}

export function isProviderBootEligible(state: HostSessionSnapshot): boolean {
	if (!isHostConfigured(state)) {
		return false;
	}

	const config = state.config;
	if (!config) {
		return false;
	}

	return (
		config.sessionKey.trim().length > 0 &&
		config.serverUrl.trim().length > 0 &&
		config.gameName.trim().length > 0 &&
		config.lang.trim().length > 0
	);
}

export function buildHostProviderBootInput(
	state: HostSessionSnapshot
): HostProviderBootInput | null {
	if (!isProviderBootEligible(state) || !state.config) {
		return null;
	}

	return {
		sessionKey: state.config.sessionKey,
		serverUrl: state.config.serverUrl,
		gameName: state.config.gameName,
		lang: state.config.lang,
		operator: state.config.operator,
		platform: state.config.platform,
		orientation: state.config.orientation,
		integrationType: state.config.integrationType,
	};
}